var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Get WordPress REST API base URL
function getApiUrl() {
    const wpData = window.wpData;
    if (wpData === null || wpData === void 0 ? void 0 : wpData.apiUrl) {
        // Convert wp/v2/ to noads/v1/
        return wpData.apiUrl.replace('wp/v2/', 'noads/v1/');
    }
    // Fallback to default WordPress REST API URL
    return '/wp-json/noads/v1/';
}
// Get WordPress nonce for CSRF protection
function getNonce() {
    const wpData = window.wpData;
    return (wpData === null || wpData === void 0 ? void 0 : wpData.nonce) || '';
}
// Helper to make authenticated API requests
function apiRequest(endpoint_1) {
    return __awaiter(this, arguments, void 0, function* (endpoint, options = {}) {
        const url = getApiUrl() + endpoint;
        const nonce = getNonce();
        const defaultOptions = Object.assign({ credentials: 'include', headers: Object.assign(Object.assign({ 'Content-Type': 'application/json' }, (nonce && { 'X-WP-Nonce': nonce })), options.headers) }, options);
        return fetch(url, defaultOptions);
    });
}
// Helper to parse WordPress REST API error responses
function parseErrorResponse(response) {
    if (response === null || response === void 0 ? void 0 : response.message) {
        return response.message;
    }
    if (response === null || response === void 0 ? void 0 : response.code) {
        return response.code;
    }
    if (typeof response === 'string') {
        return response;
    }
    return 'An error occurred. Please try again.';
}
function getInitials(name) {
    if (!name || typeof name !== 'string') {
        return 'U'; // Default fallback
    }
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
}
export function getCurrentUser() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Always check WordPress session via API - cookies are source of truth
            const response = yield apiRequest('auth/me');
            // Use response.ok to handle all 2xx status codes
            if (response.ok) {
                try {
                    // Get text first to check if it's empty (Response can only be read once)
                    const text = yield response.text();
                    // Check if response body is empty
                    if (!text || text.trim().length === 0) {
                        console.warn("getCurrentUser: Empty response body");
                        return null;
                    }
                    // Parse the JSON
                    const data = JSON.parse(text);
                    // Validate that we have a user object with required fields
                    if (data && typeof data === 'object' && data.id && data.username && data.email) {
                        return data;
                    }
                    else {
                        console.warn("getCurrentUser: Invalid user data structure", data);
                        return null;
                    }
                }
                catch (jsonError) {
                    console.error("getCurrentUser: Failed to parse JSON response", jsonError);
                    return null;
                }
            }
            else {
                // Log non-200 responses for debugging (but not 401/403 which are expected when not logged in)
                if (response.status !== 401 && response.status !== 403) {
                    console.warn(`getCurrentUser: Unexpected status ${response.status}`);
                }
                return null;
            }
        }
        catch (error) {
            console.error("Error loading current user:", error);
            return null;
        }
    });
}
export function loginUser(usernameOrEmail_1, password_1) {
    return __awaiter(this, arguments, void 0, function* (usernameOrEmail, password, rememberMe = false) {
        try {
            const response = yield apiRequest('auth/login', {
                method: 'POST',
                body: JSON.stringify({
                    username: usernameOrEmail,
                    password: password,
                    remember: rememberMe,
                }),
            });
            // Check response status first
            if (!response.ok) {
                // Try to get error message
                try {
                    const text = yield response.text();
                    if (text && text.trim().length > 0) {
                        const data = JSON.parse(text);
                        const errorMessage = parseErrorResponse(data);
                        return { success: false, error: errorMessage };
                    }
                }
                catch (e) {
                    // If we can't parse error, use status text
                }
                return {
                    success: false,
                    error: response.status === 403
                        ? "Cookie check failed. Please try again."
                        : `Login failed (${response.status})`
                };
            }
            // Parse successful response
            try {
                const text = yield response.text();
                if (!text || text.trim().length === 0) {
                    return { success: false, error: "Empty response from server" };
                }
                const data = JSON.parse(text);
                if (data && typeof data === 'object' && data.id && data.username && data.email) {
                    // WordPress sets cookies automatically via wp_signon
                    return { success: true, user: data };
                }
                else {
                    return { success: false, error: "Invalid response format" };
                }
            }
            catch (jsonError) {
                console.error("Login: Failed to parse JSON response", jsonError);
                return { success: false, error: "Invalid response from server" };
            }
        }
        catch (error) {
            console.error("Login error:", error);
            return { success: false, error: "An error occurred. Please try again." };
        }
    });
}
export function registerUser(username, email, password, displayName) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield apiRequest('auth/register', {
                method: 'POST',
                body: JSON.stringify({
                    username: username,
                    email: email,
                    password: password,
                    display_name: displayName || username,
                }),
            });
            // Check response status first
            if (!response.ok) {
                try {
                    const text = yield response.text();
                    if (text && text.trim().length > 0) {
                        const data = JSON.parse(text);
                        const errorMessage = parseErrorResponse(data);
                        return { success: false, error: errorMessage };
                    }
                }
                catch (e) {
                    // If we can't parse error, use status text
                }
                return {
                    success: false,
                    error: `Registration failed (${response.status})`
                };
            }
            // Parse successful response
            try {
                const text = yield response.text();
                if (!text || text.trim().length === 0) {
                    return { success: false, error: "Empty response from server" };
                }
                const data = JSON.parse(text);
                if (data && typeof data === 'object' && data.id && data.username && data.email) {
                    // WordPress automatically logs in the user after registration
                    return { success: true, user: data };
                }
                else {
                    return { success: false, error: "Invalid response format" };
                }
            }
            catch (jsonError) {
                console.error("Registration: Failed to parse JSON response", jsonError);
                return { success: false, error: "Invalid response from server" };
            }
        }
        catch (error) {
            console.error("Registration error:", error);
            return { success: false, error: "An error occurred. Please try again." };
        }
    });
}
export function logoutUser() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Call WordPress logout endpoint to clear cookies
            yield apiRequest('auth/logout', {
                method: 'POST',
            });
        }
        catch (error) {
            console.error("Logout error:", error);
        }
    });
}
export function getUserInitials(user) {
    if (!user) {
        return 'U'; // Default fallback
    }
    // Extra defensive check - handle undefined, null, empty string, or non-string
    const displayName = user.displayName;
    if (!displayName || typeof displayName !== 'string' || displayName.trim().length === 0) {
        return 'U'; // Default fallback
    }
    return getInitials(displayName);
}
// Helper to check if user is logged in
export function isAuthenticated() {
    return __awaiter(this, void 0, void 0, function* () {
        const user = yield getCurrentUser();
        return user !== null;
    });
}
